import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  FileText,
  Search,
  Download,
  ChevronDown,
  ChevronRight,
  Building2,
  Calendar,
  Filter,
  AlertCircle,
  Loader2
} from 'lucide-react';

export const ProformasPendientes = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  
  // Filtros
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [clienteFilter, setClienteFilter] = useState('');
  
  // Vista expandida por cliente
  const [expandedClientes, setExpandedClientes] = useState({});
  const [viewMode, setViewMode] = useState('resumen'); // 'resumen' o 'detalle'

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (yearFilter && yearFilter !== 'todos') {
        params.append('year', yearFilter);
      }
      if (clienteFilter) {
        params.append('cliente', clienteFilter);
      }
      
      const res = await api.get(`/proformas-pendientes?${params.toString()}`);
      setData(res.data);
    } catch (err) {

      setError(err.response?.data?.detail || 'Error al cargar proformas pendientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    loadData();
  };

  const toggleCliente = (cliente) => {
    setExpandedClientes(prev => ({
      ...prev,
      [cliente]: !prev[cliente]
    }));
  };

  const formatMoney = (value) => {
    if (!value && value !== 0) return '-';
    return '$' + Math.round(value).toLocaleString('es-CL');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    // Formato MM/DD/YY -> DD/MM/YY
    const parts = dateStr.split('/');
    if (parts.length >= 3) {
      return `${parts[1]}/${parts[0]}/${parts[2]}`;
    }
    return dateStr;
  };

  const getProformasCliente = (clienteNombre) => {
    if (!data?.proformas) return [];
    return data.proformas.filter(p => p.cliente_nombre === clienteNombre);
  };

  const exportToCSV = () => {
    if (!data?.proformas) return;
    
    const headers = ['Proforma', 'Factura', 'Fecha', 'Cliente', 'Neto', 'IVA', 'Total', 'O/C', 'OT'];
    const rows = data.proformas.map(p => [
      p.proforma,
      p.numero_factura,
      formatDate(p.fecha),
      p.cliente_nombre,
      p.neto,
      p.iva,
      p.total,
      p.orden_compra,
      p.ot
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(c => `"${c}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `proformas_pendientes_${yearFilter || 'todos'}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Proformas Pendientes</h1>
          <p className="text-sm text-muted-foreground">
            Proformas en Access que aún no están facturadas en Alegra
          </p>
        </div>
        
        <Button variant="outline" onClick={exportToCSV} disabled={!data?.proformas?.length}>
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-blue-600" />
          <h2 className="font-semibold">Filtros</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Año</label>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger data-testid="year-filter">
                <SelectValue placeholder="Seleccionar año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los años</SelectItem>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Cliente</label>
            <Input
              placeholder="Buscar cliente..."
              value={clienteFilter}
              onChange={(e) => setClienteFilter(e.target.value)}
              data-testid="cliente-filter"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Vista</label>
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="resumen">Resumen por Cliente</SelectItem>
                <SelectItem value="detalle">Detalle Completo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-end">
            <Button onClick={handleSearch} disabled={loading} className="w-full">
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Totales */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-blue-600 mb-2">
              <FileText className="w-5 h-5" />
              <span className="font-medium">Proformas Pendientes</span>
            </div>
            <p className="text-2xl font-bold">{data.totales?.cantidad || 0}</p>
          </div>
          
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-green-600 mb-2">
              <Building2 className="w-5 h-5" />
              <span className="font-medium">Total Neto</span>
            </div>
            <p className="text-2xl font-bold">{formatMoney(data.totales?.neto)}</p>
          </div>
          
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-purple-600 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">Total con IVA</span>
            </div>
            <p className="text-2xl font-bold">{formatMoney(data.totales?.total)}</p>
          </div>
          
          <div className="bg-white rounded-lg border p-4">
            <div className="flex items-center gap-2 text-orange-600 mb-2">
              <FileText className="w-5 h-5" />
              <span className="font-medium">Ya en Alegra</span>
            </div>
            <p className="text-2xl font-bold">{data.facturas_en_alegra || 0}</p>
            <p className="text-xs text-muted-foreground">Facturas sincronizadas</p>
          </div>
        </div>
      )}

      {/* Contenido Principal */}
      {loading ? (
        <div className="bg-white rounded-lg border p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-2">Cargando proformas...</span>
        </div>
      ) : data ? (
        viewMode === 'resumen' ? (
          /* Vista Resumen por Cliente */
          <div className="bg-white rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Neto</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.resumen_por_cliente?.map((cliente, _idx) => (
                  <>
                    <TableRow 
                      key={cliente.cliente}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleCliente(cliente.cliente)}
                    >
                      <TableCell>
                        {expandedClientes[cliente.cliente] ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{cliente.cliente}</TableCell>
                      <TableCell className="text-right">{cliente.count}</TableCell>
                      <TableCell className="text-right">{formatMoney(cliente.neto)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatMoney(cliente.total)}</TableCell>
                    </TableRow>
                    
                    {/* Detalle expandido */}
                    {expandedClientes[cliente.cliente] && (
                      <TableRow>
                        <TableCell colSpan={5} className="bg-gray-50 p-0">
                          <div className="p-4">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Proforma</TableHead>
                                  <TableHead>Fact#</TableHead>
                                  <TableHead>Fecha</TableHead>
                                  <TableHead>O/C</TableHead>
                                  <TableHead>OT</TableHead>
                                  <TableHead className="text-right">Neto</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {getProformasCliente(cliente.cliente).map((p, i) => (
                                  <TableRow key={i}>
                                    <TableCell className="font-mono text-sm">{p.proforma}</TableCell>
                                    <TableCell>{p.numero_factura}</TableCell>
                                    <TableCell>{formatDate(p.fecha)}</TableCell>
                                    <TableCell>{p.orden_compra}</TableCell>
                                    <TableCell>{p.ot}</TableCell>
                                    <TableCell className="text-right">{formatMoney(p.neto)}</TableCell>
                                    <TableCell className="text-right">{formatMoney(p.total)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))}
                
                {/* Fila de totales */}
                <TableRow className="bg-blue-50 font-bold">
                  <TableCell></TableCell>
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right">{data.totales?.cantidad}</TableCell>
                  <TableCell className="text-right">{formatMoney(data.totales?.neto)}</TableCell>
                  <TableCell className="text-right">{formatMoney(data.totales?.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        ) : (
          /* Vista Detalle Completo */
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>#</TableHead>
                    <TableHead>Proforma</TableHead>
                    <TableHead>Fact#</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>O/C</TableHead>
                    <TableHead>OT</TableHead>
                    <TableHead className="text-right">Neto</TableHead>
                    <TableHead className="text-right">IVA</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.proformas?.map((p, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                      <TableCell className="font-mono text-sm">{p.proforma}</TableCell>
                      <TableCell>{p.numero_factura}</TableCell>
                      <TableCell>{formatDate(p.fecha)}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={p.cliente_nombre}>
                        {p.cliente_nombre}
                      </TableCell>
                      <TableCell>{p.orden_compra}</TableCell>
                      <TableCell>{p.ot}</TableCell>
                      <TableCell className="text-right">{formatMoney(p.neto)}</TableCell>
                      <TableCell className="text-right">{formatMoney(p.iva)}</TableCell>
                      <TableCell className="text-right font-semibold">{formatMoney(p.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )
      ) : null}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Información</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Esta consulta compara proformas de Access (Ventas 1) contra facturas sincronizadas de Alegra</li>
          <li>• Las proformas mostradas tienen número de proforma pero su factura aún no está en Alegra</li>
          <li>• Facturas con número 99999 son proformas que aún no tienen factura asignada</li>
          <li>• Use el filtro de año para ver solo proformas de un período específico</li>
          <li>• Haga clic en un cliente para ver el detalle de sus proformas</li>
        </ul>
      </div>
    </div>
  );
};

export default ProformasPendientes;
